import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import { useAuthStore } from "@/store/auth.store";

/**
 * RequireAuth — protects internal app routes.
 * Redirects to /login when not authenticated.
 * Shows nothing while session check is in progress.
 */
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, hasChecked, checkSession } = useAuthStore();
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    // S-079: always revalidate the session once on mount — even when Zustand
    // has a persisted `isAuthenticated`. Persisted auth can be stale (the
    // server session may have expired), and trusting it blindly meant an
    // expired user was only caught reactively on the next 401. checkSession
    // dedups (hasChecked/isLoading guard) so this runs at most once, and it
    // only clears auth on a real 401/403 — transient 5xx/network errors keep
    // the current state. Children still render optimistically for a persisted
    // user (see the loader guard below), so there's no flash.
    if (!hasChecked) {
      checkSession();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Only block on the loader when we have NO known auth yet. A persisted-authed
  // user renders immediately while revalidation runs in the background; if that
  // revalidation clears auth, the redirect below fires.
  if (isLoading && !isAuthenticated) {
    // Show minimal loader while checking session
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          backgroundColor: "var(--bg-page)",
          color: "var(--text-muted)",
          fontFamily: "'DM Sans', sans-serif",
          fontSize: "14px",
        }}
      >
        {t("common.loading")}
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
